import { type FC, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Link,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import type { AdminDashboardOrganizationRow } from "@/admin/entities/adminDashboard";
import {
  buildOrganizationRows,
  formatCount,
  formatPercent,
  formatUsd,
  organizationHref,
  organizationTotals,
  sortOrganizationRows,
  trendTone,
  type OrganizationSortKey,
  type SortDirection,
} from "../model";
import { QueryErrorAlert } from "./QueryErrorAlert";
import { TrendChip } from "./TrendChip";

interface OrganizationsTableProps {
  rows: AdminDashboardOrganizationRow[];
  totalRevenueShareUSD: number;
  hasComparison: boolean;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

const COLUMNS: {
  key: OrganizationSortKey;
  label: string;
  align: "left" | "right";
}[] = [
  { key: "title", label: "Организация", align: "left" },
  { key: "salesUSD", label: "Продажи", align: "right" },
  { key: "revenueShareUSD", label: "Наш ревшер", align: "right" },
  { key: "paidInvoicesUSD", label: "Оплачено счетов", align: "right" },
  { key: "views", label: "Просмотры", align: "right" },
  { key: "share", label: "Доля в ревшере", align: "left" },
  { key: "trend", label: "Тренд продаж", align: "right" },
];

const clampPercent = (value: number | null) =>
  Math.max(0, Math.min(100, value ?? 0));

/** Организации с активностью в периоде (спека §3.4), Card и MUI Table. */
export const OrganizationsTable: FC<OrganizationsTableProps> = ({
  rows,
  totalRevenueShareUSD,
  hasComparison,
  loading,
  error,
  onRetry,
}) => {
  const [sort, setSort] = useState<{
    key: OrganizationSortKey;
    direction: SortDirection;
  }>({ key: "salesUSD", direction: "desc" });

  const tableRows = useMemo(
    () =>
      sortOrganizationRows(
        buildOrganizationRows(rows, totalRevenueShareUSD, hasComparison),
        sort.key,
        sort.direction
      ),
    [rows, totalRevenueShareUSD, hasComparison, sort]
  );
  const totals = useMemo(() => organizationTotals(rows), [rows]);

  // Новая колонка сортируется по убыванию, повторный клик переворачивает.
  const toggleSort = (key: OrganizationSortKey) =>
    setSort((current) => ({
      key,
      direction:
        current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));

  let body;
  if (error) {
    body = (
      <CardContent>
        <QueryErrorAlert onRetry={onRetry} />
      </CardContent>
    );
  } else if (loading) {
    body = (
      <CardContent>
        <Skeleton variant="rounded" height={240} />
      </CardContent>
    );
  } else if (rows.length === 0) {
    body = (
      <CardContent>
        <Typography color="text.secondary">Нет активности за период</Typography>
      </CardContent>
    );
  } else {
    body = (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => {
                const active = sort.key === column.key;
                return (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    sortDirection={active ? sort.direction : false}
                  >
                    <TableSortLabel
                      active={active}
                      direction={active ? sort.direction : "desc"}
                      onClick={() => toggleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.map((row) => (
              <TableRow key={row.organizationId} hover>
                <TableCell>
                  <Link
                    component={RouterLink}
                    to={organizationHref(row.organizationId)}
                  >
                    {row.title}
                  </Link>
                </TableCell>
                <TableCell align="right">{formatUsd(row.salesUSD)}</TableCell>
                <TableCell align="right">
                  {formatUsd(row.revenueShareUSD)}
                </TableCell>
                <TableCell align="right">
                  {formatUsd(row.paidInvoicesUSD)}
                </TableCell>
                <TableCell align="right">{formatCount(row.views)}</TableCell>
                <TableCell sx={{ minWidth: 160 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    <LinearProgress
                      variant="determinate"
                      value={clampPercent(row.share)}
                      sx={{ flexGrow: 1 }}
                    />
                    <Typography variant="body2" sx={{ minWidth: 48 }}>
                      {row.share === null ? "" : formatPercent(row.share)}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <TrendChip
                    trend={row.trend}
                    tone={trendTone(row.trend, "higherIsBetter")}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell>Итого</TableCell>
              <TableCell align="right">{formatUsd(totals.salesUSD)}</TableCell>
              <TableCell align="right">
                {formatUsd(totals.revenueShareUSD)}
              </TableCell>
              <TableCell align="right">
                {formatUsd(totals.paidInvoicesUSD)}
              </TableCell>
              <TableCell align="right">{formatCount(totals.views)}</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Организации"
        subheader="Активность за выбранный период"
      />
      {body}
    </Card>
  );
};
